import { AccountCircle, Logout, Settings } from "@mui/icons-material";
import {
  Box,
  CssBaseline,
  Divider,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { openCookieSettings } from "../utils/cookie-consent";
import { useHasCookieConsent } from "../hooks/useHasCookieConsent";
import { PageLoading } from "../components/system/LoadingStates";
import React, { useState, useSyncExternalStore } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import Sidebar from "../components/layout/Sidebar";
import { CopyrightNotice } from "../components/common/CopyrightNotice";
import ToastSystem from "../components/notifications/ToastSystem";
import SupportWrapper from "../components/support/SupportWrapper";
import { useAuth } from "../contexts/AuthContext";
import { GlobalLoader } from "../components/common/GlobalLoader";
import { cardGateController } from "../services/card-gate/card-gate-controller";
import { useIsMutating } from "@tanstack/react-query";
import OfflineMode from "../components/system/OfflineMode";
import MobileBottomNavigation from "../components/mobile/MobileBottomNavigation";
import TouchGestureWrapper from "../components/mobile/TouchGestureWrapper";

// Вне компонента: `useSyncExternalStore` переподписывается при новой ссылке.
const subscribeCardGate = (onChange: () => void) =>
  cardGateController.subscribe(onChange);
const readCardGateOpen = () => cardGateController.isOpen();

interface MainLayoutProps {
  window?: () => Window;
}

const MainLayout: React.FC<MainLayoutProps> = ({ window: _window }) => {
  // Сначала получаем данные из контекста
  const { user, logout, isLoading } = useAuth();
  const { t } = useTranslation("common");
  const hasCookieConsent = useHasCookieConsent();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMutating = useIsMutating();
  const cardGateOpen = useSyncExternalStore(
    subscribeCardGate,
    readCardGateOpen,
  );

  // Если идёт загрузка или пользователя нет — показываем загрузку
  if (isLoading || !user) {
    return <PageLoading message="Загрузка интерфейса..." />;
  }

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
  //   setAnchorEl(event.currentTarget);
  // };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate("/auth/login");
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate("/settings/profile");
  };

  return (
    <TouchGestureWrapper
      onSwipeLeft={() => navigate("/events")}
      onSwipeRight={() => navigate("/dashboard")}
      onSwipeUp={() => {
        if (typeof globalThis !== "undefined" && "scrollTo" in globalThis) {
          (globalThis as unknown as Window).scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }
      }}
      onSwipeDown={() => {
        if (
          typeof document !== "undefined" &&
          typeof globalThis !== "undefined" &&
          "scrollTo" in globalThis
        ) {
          const h = document.body?.scrollHeight || 0;
          (globalThis as unknown as Window).scrollTo({
            top: h,
            behavior: "smooth",
          });
        }
      }}
      enablePinch={false}
    >
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Header
          sidebarOpen={sidebarOpen}
          onSidebarToggle={handleSidebarToggle}
        />
        <Sidebar
          open={sidebarOpen}
          onToggle={handleSidebarToggle}
          isMobile={isMobile}
        />
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleProfileClick}>
            <ListItemIcon>
              <AccountCircle fontSize="small" />
            </ListItemIcon>
            Мой профиль
          </MenuItem>
          <MenuItem onClick={handleMenuClose}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            Настройки
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Выйти
          </MenuItem>
        </Menu>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            minHeight: "100vh",
            paddingTop: "80px",
            minWidth: 0,
          }}
        >
          <Outlet />
          <Box component="footer" sx={{ mt: "auto", pt: 4, pb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              display="block"
              sx={{ mb: 0.5 }}
            >
              <Link href="/privacy-policy" color="inherit" underline="hover">
                {t("auth.privacyPolicy")}
              </Link>
              {" | "}
              <Link
                component="button"
                variant="caption"
                color="inherit"
                underline="hover"
                onClick={openCookieSettings}
                sx={{
                  cursor: "pointer",
                  background: "none",
                  border: "none",
                }}
              >
                {t("auth.doNotSell")}
              </Link>
              {hasCookieConsent && (
                <>
                  {" | "}
                  <Link
                    component="button"
                    variant="caption"
                    color="inherit"
                    underline="hover"
                    onClick={openCookieSettings}
                    sx={{
                      cursor: "pointer",
                      background: "none",
                      border: "none",
                    }}
                  >
                    {t("auth.cookieSettings")}
                  </Link>
                </>
              )}
            </Typography>
            <CopyrightNotice />
          </Box>
        </Box>
        <ToastSystem />
        <SupportWrapper />
        {/* AD-778. Запрос с ответом 402 ждёт окна карты, и мутация висит. Пока окно
            открыто, слой не показывается: он закрыл бы окно, которого ждёт запрос. */}
        <GlobalLoader
          isVisible={isMutating > 0 && !cardGateOpen}
          message={t("saving")}
        />
        <OfflineMode />
        <MobileBottomNavigation />
      </Box>
    </TouchGestureWrapper>
  );
};

export default MainLayout;
